import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getProductWeight } from 'shared/helpers/products';
import { formatDiscountForDisplay } from 'shared/helpers/specials';
import { Product } from 'utils/helpers/product';

import {
  calcDiscountPercentage,
  getIsSpecialApplicable,
  getProductSpecialsKeys,
  SpecialRestriction,
  transformSpecialRestrictions,
} from './specials.utils';

import { formatPrice, formatDiscount, majorityCalculationMethod } from './pricing.utils';

import { UsePriceOptionsProps, UsePriceOptionsReturn, PriceOption, Special } from './use-product-pricing.types';

type PriceOptionFilterProps = Pick<
  Product,
  'limitsPerCustomer' | 'manualInventory' | 'optionsBelowKioskThreshold' | 'optionsBelowThreshold'
> & {
  isKiosk: boolean;
};

type PriceOptionFilter = (option: PriceOption) => boolean;

type TransformOptionsProps = {
  options: Product['Options'];
  prices: Product['medicalPrices'] | Product['recPrices'];
  product: Product;
  specialPrices: Product['medicalSpecialPrices'] | Product['recSpecialPrices'];
  specialRestrictions: SpecialRestriction[];
  priceOptionFilter: PriceOptionFilter;
  // True only when the server's applyPOSPrices actually overwrote this product/menuType's price
  // arrays this request (product.posSyncedPricingApplied, stamped server-side -- not inferred from
  // POSPricingData bucket presence, which can be stale). When true, `special` is decided purely by
  // the specialPrice-vs-price diff -- legacy specialData restrictions are irrelevant for POS-synced
  // discounts (they have no saleSpecials entry at all) and are ignored rather than OR'd in, so this
  // can't widen scope for non-POS-synced products. When false, behavior is exactly the original
  // getIsSpecialApplicable-only check.
  usesPosSyncedPricing: boolean;
  // The majority calculation method (see majorityCalculationMethod in pricing.utils.ts) across this
  // menuType's POSPricingData bucket.discounts -- only meaningful when usesPosSyncedPricing is true.
  // 'AMOUNT' formats as "$X off"; 'PERCENT' or unresolved/null falls back to "X% off". Legacy
  // (non-POS-synced) discounts ignore this entirely.
  posCalculationMethod: 'AMOUNT' | 'PERCENT' | null;
  // growth.ecomm.pricingTierSavings kill switch (PF-9530). Gates addTierSavings so it can be
  // disabled without a deploy if the savings math misbehaves for some catalog shape.
  tierSavingsEnabled: boolean;
};

function isArrayOfString(value: any): value is Array<string> {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isArrayOfNumber(value: any): value is Array<number> {
  return Array.isArray(value) && value.every((item) => typeof item === 'number');
}

function getLimitPerCustomerPerOption(
  limitsPerCustomer: Product['limitsPerCustomer'],
  optionValue: string
): number | null {
  if (limitsPerCustomer && Array.isArray(limitsPerCustomer)) {
    const optionLimit = limitsPerCustomer.find((item) => item?.key === optionValue);

    if (optionLimit?.value && optionLimit.value !== 'Unlimited') {
      return parseInt(optionLimit.value, 10);
    }
  }

  return null;
}

function getSpecial(specialPrice: number, price: number): Special {
  const discount = calcDiscountPercentage(specialPrice, price);

  return {
    price: specialPrice,
    formattedPrice: formatPrice(specialPrice),
    discount,
    formattedDiscount: formatDiscount(discount),
  };
}

// Catalog options are one complete weight token (optional "Nx" pack prefix), never a substring
// match. Shared isGram/isOz helpers are unanchored, so using them here would let a malformed
// value like "pack-1g-deal" under-parse and become the baseline for every other tier.
type WeightFamily = 'mg' | 'weight';

const EXACT_WEIGHT_TOKEN = /^(?:(\d+)x)?(\d*\.?\d+mg|\d*\.?\d+gC|\d*\.?\d+g|\d\/\d{1,2}oz|\d*\.?\d+oz)$/i;

function getWeightFamily(token: string): WeightFamily | null {
  if (/mg$/i.test(token)) {
    return 'mg';
  }

  if (/(gC|g|oz)$/i.test(token)) {
    return 'weight';
  }

  return null;
}

function getComparableWeight(value: string): { family: WeightFamily; weight: number } | null {
  const normalizedValue = value.replace(/\s/g, '');
  const match = EXACT_WEIGHT_TOKEN.exec(normalizedValue);
  if (!match) {
    return null;
  }

  const family = getWeightFamily(match[2]);
  if (!family) {
    return null;
  }

  const weight = getProductWeight(normalizedValue, 0, 1);
  return Number.isFinite(weight) && weight > 0 ? { family, weight } : null;
}

function addTierSavings(priceOptions: PriceOption[]): PriceOption[] {
  const comparableOptions = priceOptions
    .map((option) => {
      const parsed = getComparableWeight(option.value);
      return parsed ? { option, ...parsed } : null;
    })
    .filter(
      (entry): entry is { option: PriceOption; family: WeightFamily; weight: number } =>
        entry !== null && Number.isFinite(entry.option.price) && entry.option.price > 0
    );

  // mg (edibles/concentrates) and g/oz (flower) are not economically comparable per gram even
  // though getProductWeight can convert both. Real products use one unit family; mixed arrays
  // fail closed rather than invent a cross-category baseline.
  const families = new Set(comparableOptions.map((entry) => entry.family));
  if (families.size !== 1) {
    return priceOptions;
  }

  const baseline = comparableOptions.reduce<{ option: PriceOption; weight: number } | null>(
    (smallest, entry) => (!smallest || entry.weight < smallest.weight ? entry : smallest),
    null
  );

  if (!baseline) {
    return priceOptions;
  }

  const baselineUnitPrice = baseline.option.price / baseline.weight;
  if (!Number.isFinite(baselineUnitPrice) || baselineUnitPrice <= 0) {
    return priceOptions;
  }

  return priceOptions.map((option) => {
    const parsed = getComparableWeight(option.value);
    if (parsed === null || parsed.weight <= baseline.weight || !Number.isFinite(option.price) || option.price <= 0) {
      return option;
    }

    // Floor so a consumer-facing savings claim never rounds up (49.6% must not read as 50% off).
    // A tiny epsilon offsets floating-point values just below a whole percent (19.999... -> 20).
    const savingsPercent = Math.floor((1 - option.price / parsed.weight / baselineUnitPrice) * 100 + 1e-9);
    if (!Number.isFinite(savingsPercent) || savingsPercent <= 0) {
      return option;
    }

    return {
      ...option,
      formattedTierSavings: `${savingsPercent}% off`,
      tierSavingsPercent: savingsPercent,
    };
  });
}

function transformOptions({
  options,
  prices,
  product,
  specialPrices,
  specialRestrictions,
  priceOptionFilter,
  usesPosSyncedPricing,
  posCalculationMethod,
  tierSavingsEnabled,
}: TransformOptionsProps): PriceOption[] {
  if (!isArrayOfString(options) || !isArrayOfNumber(prices) || !isArrayOfNumber(specialPrices)) {
    return [];
  }

  const uniqPriceOptions: Record<string, PriceOption> = {};

  options.forEach((value, idx) => {
    const price = prices[idx];
    const specialPrice = specialPrices[idx];
    const hasSpecialPriceDiff = Boolean(specialPrice) && specialPrice < price;
    const isSpecial = usesPosSyncedPricing ? hasSpecialPriceDiff : getIsSpecialApplicable(value, specialRestrictions);

    const label = value.replace(`oz`, ` oz`).replace(`gC`, `g`);
    const uniqKey = value.replace('C', '');

    uniqPriceOptions[uniqKey] = {
      value,
      label,
      price,
      formattedDiscount: formatDiscountForDisplay(
        price,
        specialPrice,
        product,
        usesPosSyncedPricing ? 'sale' : null,
        value,
        undefined,
        usesPosSyncedPricing && posCalculationMethod === 'AMOUNT'
      ),
      formattedPrice: formatPrice(price),
      formattedTierSavings: null,
      special: specialPrice && isSpecial ? getSpecial(specialPrice, price) : null,
      tierSavingsPercent: null,
    };
  });

  const filteredOptions = Object.values(uniqPriceOptions).filter(priceOptionFilter);
  return tierSavingsEnabled ? addTierSavings(filteredOptions) : filteredOptions;
}

function getPriceOptionFilter({
  isKiosk,
  limitsPerCustomer,
  optionsBelowThreshold,
  optionsBelowKioskThreshold,
  manualInventory,
}: PriceOptionFilterProps): PriceOptionFilter {
  return ({ value }) => {
    if (getLimitPerCustomerPerOption(limitsPerCustomer, value) === 0) {
      return false;
    }

    if (!isKiosk && optionsBelowThreshold?.includes(value)) {
      return false;
    }

    if (isKiosk && optionsBelowKioskThreshold?.includes(value)) {
      return false;
    }

    if (manualInventory?.length) {
      const inventoryOption = manualInventory.find((item) => item?.option === value);

      if (inventoryOption?.inventory) {
        return inventoryOption.inventory > 0;
      }
    }

    return true;
  };
}

/*
  This is a refactor of:
    - src/utils/helpers/product.js: groupOptionsWithPrices
    - shared/helpers/products.js: formatWeightOptions

  TODO:
   - implement `showQualifyingOptions` when needed
   - implement `specialId` when needed
   - maybe memoaize results
*/
export function usePriceOptions({ menuType, isKiosk }: UsePriceOptionsProps): UsePriceOptionsReturn {
  const flags = useFlags();
  const tierSavingsEnabled = flags['growth.ecomm.pricingTierSavings'] ?? false;

  const getPriceOptions = useCallback(
    (product: Product) => {
      const {
        limitsPerCustomer,
        manualInventory,
        medicalPrices,
        medicalSpecialPrices,
        Options: options,
        optionsBelowKioskThreshold,
        optionsBelowThreshold,
        POSPricingData,
        posSyncedPricingApplied,
        recPrices,
        recSpecialPrices,
        specialData,
      } = product;

      const isMed = menuType === 'med';

      const prices = isMed ? medicalPrices : recPrices;
      const specialPrices = isMed ? medicalSpecialPrices : recSpecialPrices;
      const usesPosSyncedPricing = Boolean(
        isMed ? posSyncedPricingApplied?.medical : posSyncedPricingApplied?.recreational
      );
      // posBucket.discounts is a pre-existing field on the POSPricingData bucket, written by
      // Armageddon's PricingSyncWriter (bucket_document) alongside conditionDiscounts/rewardDiscounts
      // -- apps/api's applyPOSPrices has just never had a reason to read it before now, so it has no
      // in-repo footprint outside this hook. Each entry's calculationMethod (PERCENT/AMOUNT) is synced
      // from leaflogix via a companion Armageddon change; until that ships and a sync re-runs, entries
      // simply lack calculationMethod, majorityCalculationMethod returns null, and this safely falls
      // back to percent formatting below -- same as any other unresolved calculation method.
      const posBucket = isMed ? POSPricingData?.medical : POSPricingData?.recreational;
      const posCalculationMethod = majorityCalculationMethod(posBucket?.discounts);

      const productSpecialsKeys = getProductSpecialsKeys(product);
      const specialRestrictions = transformSpecialRestrictions({ menuType, specialData, productSpecialsKeys });

      const priceOptionFilter = getPriceOptionFilter({
        isKiosk,
        limitsPerCustomer,
        manualInventory,
        optionsBelowKioskThreshold,
        optionsBelowThreshold,
      });

      const priceOptions = transformOptions({
        options,
        prices,
        product,
        specialPrices,
        specialRestrictions,
        priceOptionFilter,
        usesPosSyncedPricing,
        posCalculationMethod,
        tierSavingsEnabled,
      });

      return priceOptions;
    },
    [menuType, isKiosk, tierSavingsEnabled]
  );

  const getPriceOption = useCallback(
    (product: Product, optionValue: string): PriceOption | null =>
      getPriceOptions(product).find(({ value }) => value === optionValue) ?? null,
    [getPriceOptions]
  );

  return {
    getPriceOption,
    getPriceOptions,
  };
}
